import { extOf } from './utils.js';

/* Parser de listas M3U/M3U8 extendidas (#EXTINF con atributos tvg-*).
   Clasifica cada entrada como canal en directo, película o episodio de serie por la URL/extensión. */

const ATTR_RE = /([a-zA-Z0-9_-]+)="([^"]*)"/g;
const VIDEO_EXT = ['mp4', 'mkv', 'avi', 'mov', 'webm', 'flv', 'wmv', 'mpg', 'mpeg', 'm4v'];

function parseAttrs(text) {
    const attrs = {};
    let m;
    ATTR_RE.lastIndex = 0;
    while ((m = ATTR_RE.exec(text))) attrs[m[1].toLowerCase()] = m[2];
    return attrs;
}

export function classifyUrl(url, group) {
    const g = (group || '').toLowerCase();
    if (/\/series\//i.test(url) || /\b(series|serie)\b/.test(g)) return 'series';
    if (/\/movie\//i.test(url) || VIDEO_EXT.indexOf(extOf(url)) !== -1 || /\b(vod|movies?|pel[ií]culas?|films?)\b/.test(g)) return 'vod';
    return 'live';
}

/** Intenta obtener "Nombre de serie S01E02" -> {series, season, episode}. */
export function parseEpisodeName(name) {
    const m = String(name || '').match(/^(.*?)[\s._-]*S(\d{1,2})[\s._-]*E(\d{1,3})(.*)$/i);
    if (!m) return null;
    return {
        series: m[1].trim(),
        season: Number(m[2]),
        episode: Number(m[3]),
        title: (m[4] || '').replace(/^[\s:._-]+/, '').trim()
    };
}

/** Algunos proxys reescriben las direcciones de la lista para pasar por ellos: se deshace. */
function unproxy(url) {
    const m = /^(?:https?:\/\/[^/]+)?\/proxy\?(?:(?:st|ik)=[^&]*&)?url=([^&]+)(?:&(?:st|ik)=[^&]*)?$/.exec(url);
    if (!m) return url;
    try {
        return decodeURIComponent(m[1]);
    } catch (e) {
        return url;
    }
}

const slug = (text) => String(text || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Canales repetidos en una misma lista (mismo nombre y mismo identificador de guía): son
 * emisiones alternativas del mismo canal. Se queda la primera y las demás pasan a `alt`, para
 * que el reproductor las pruebe si la primera falla.
 */
export function mergeDuplicateChannels(live) {
    const seen = new Map();
    const out = [];
    live.forEach((ch) => {
        const key = ch.epgId ? ch.epgId + '|' + String(ch.name).trim().toLowerCase() : '';
        const first = key ? seen.get(key) : null;
        if (first) {
            if (first.url !== ch.url && !(first.alt || []).some((a) => a.url === ch.url)) {
                (first.alt = first.alt || []).push({ source: '', id: ch.id, name: ch.name, ext: '', url: ch.url });
            }
            return;
        }
        if (key) seen.set(key, ch);
        out.push(ch);
    });
    return out;
}

/**
 * `stableIds`: el identificador de cada canal sale de su nombre y no de su posición, para que
 * los favoritos no se descoloquen cuando la lista cambia (las listas públicas cambian a menudo).
 */
export function parseM3U(text, { stableIds = false } = {}) {
    const lines = String(text || '').split(/\r?\n/);
    // La cabecera puede indicar dónde está la guía: #EXTM3U url-tvg="…"
    let epgUrl = '';
    for (let i = 0; i < lines.length && i < 20 && !epgUrl; i++) {
        if (lines[i].indexOf('#EXTM3U') !== 0) continue;
        const a = parseAttrs(lines[i]);
        epgUrl = (a['url-tvg'] || a['x-tvg-url'] || '').split(',')[0].trim();
    }
    const used = new Map();
    const live = [];
    const vod = [];
    const seriesMap = new Map();
    const cats = { live: new Map(), vod: new Map(), series: new Map() };
    let pending = null;
    let counter = 0;

    const catId = (kind, name) => {
        const key = name || 'Sin categoría';
        if (!cats[kind].has(key)) cats[kind].set(key, { id: 'g' + (cats[kind].size + 1), name: key });
        return cats[kind].get(key).id;
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        if (line.indexOf('#EXTINF') === 0) {
            const comma = line.lastIndexOf(',');
            const head = comma === -1 ? line : line.slice(0, comma);
            const name = comma === -1 ? '' : line.slice(comma + 1).trim();
            const attrs = parseAttrs(head);
            pending = {
                name: attrs['tvg-name'] && !name ? attrs['tvg-name'] : name || attrs['tvg-name'] || '',
                logo: attrs['tvg-logo'] || '',
                epgId: (attrs['tvg-id'] || '').trim().toLowerCase(),
                group: attrs['group-title'] || '',
                num: Number(attrs['tvg-chno']) || 0
            };
            continue;
        }
        if (line[0] === '#') continue;
        if (!pending) continue;

        const url = unproxy(line);
        const kind = classifyUrl(url, pending.group);
        counter++;
        let id = 'm' + counter;
        if (stableIds) {
            const base = 'c-' + (slug(pending.epgId || '') || 'x') + '-' + (slug(pending.name) || 'x');
            const n = (used.get(base) || 0) + 1;
            used.set(base, n);
            id = n === 1 ? base : base + '-' + n;
        }

        if (kind === 'live') {
            live.push({
                id, num: pending.num || live.length + 1, name: pending.name, logo: pending.logo,
                categoryId: catId('live', pending.group), epgId: pending.epgId, archive: false, archiveDays: 0, added: 0, url
            });
        } else if (kind === 'vod') {
            vod.push({
                id, name: pending.name, poster: pending.logo, logoArt: true, categoryId: catId('vod', pending.group),
                ext: extOf(url) || 'mp4', added: 0, rating: 0, year: '', plot: '', genre: '', duration: '', url
            });
        } else {
            const ep = parseEpisodeName(pending.name) || { series: pending.name, season: 1, episode: 1, title: '' };
            const key = ep.series.toLowerCase();
            if (!seriesMap.has(key)) {
                seriesMap.set(key, {
                    id: 's' + (seriesMap.size + 1), name: ep.series, poster: pending.logo, logoArt: true, backdrop: '',
                    categoryId: catId('series', pending.group), plot: '', cast: '', director: '', genre: '',
                    releaseDate: '', year: '', rating: 0, added: 0, youtube: '', seasons: []
                });
            }
            const s = seriesMap.get(key);
            let season = s.seasons.find((x) => x.number === ep.season);
            if (!season) {
                season = { number: ep.season, name: 'Temporada ' + ep.season, poster: '', overview: '', episodes: [] };
                s.seasons.push(season);
            }
            season.episodes.push({
                id, seriesId: s.id, season: ep.season, num: ep.episode, title: ep.title || pending.name,
                ext: extOf(url) || 'mp4', plot: '', durationSecs: 0, duration: '', thumb: pending.logo, rating: 0, airDate: '', url
            });
        }
        pending = null;
    }

    const series = Array.from(seriesMap.values()).map((s) => {
        s.seasons.sort((a, b) => a.number - b.number);
        s.seasons.forEach((se) => se.episodes.sort((a, b) => a.num - b.num));
        return s;
    });

    const toList = (map) => Array.from(map.values());
    return {
        live: mergeDuplicateChannels(live), vod, series, epgUrl,
        categories: { live: toList(cats.live), vod: toList(cats.vod), series: toList(cats.series) }
    };
}
